import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Archive, Download, Search, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const RISK_STYLES = {
  critical: 'bg-red-100 text-red-800 border-red-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  medium: 'bg-amber-100 text-amber-800 border-amber-200',
  low: 'bg-sky-100 text-sky-800 border-sky-200',
  clear: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

function riskBadge(level) {
  const cls = RISK_STYLES[level] || 'bg-slate-100 text-slate-700 border-slate-200';
  return <Badge className={`${cls} text-xs uppercase font-mono`}>{level}</Badge>;
}

export default function ComplianceArchivePage({ user, onLogout }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [risk, setRisk] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(q.trim()), 400);
    return () => clearTimeout(timer);
  }, [q]);

  const fetchRows = useCallback(async (signal) => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (debouncedQ) params.q = debouncedQ;
      if (risk) params.risk_level = risk;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const { data } = await axios.get(`${API}/compliance/archive`, {
        headers: getAuthHeaders(),
        params,
        signal,
      });
      setRows(data.reports || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load archive';
      toast.error(typeof detail === 'string' ? detail : 'Failed to load archive', {
        id: 'compliance-archive-load-error',
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, risk, startDate, endDate]);

  useEffect(() => {
    const controller = new AbortController();
    fetchRows(controller.signal);
    return () => controller.abort();
  }, [fetchRows]);

  const downloadRow = async (row) => {
    try {
      const response = await axios.get(
        `${API}/compliance/archive/${row.id}/download`,
        { headers: getAuthHeaders(), responseType: 'blob' },
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance-report-${(row.scan_id || row.id).slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      const signature = response.headers?.['x-report-signature'];
      toast.success(
        `Downloaded — signature ${signature ? signature.slice(0, 12) : '—'}…`,
        { id: `archive-dl-${row.id}` },
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to download report';
      toast.error(typeof detail === 'string' ? detail : 'Failed to download report');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} activePage="compliance-archive">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="compliance-archive-page">
        <div className="flex items-center gap-3">
          <Archive className="w-6 h-6 text-slate-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Compliance Archive</h1>
            <p className="text-sm text-slate-500">
              Every generated PDF, HMAC-signed &amp; searchable for BNM audit binders.
            </p>
          </div>
        </div>

        <Card className="border border-slate-200">
          <CardContent className="pt-5">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div className="md:col-span-2 relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="archive-search-input"
                  placeholder="Search subject or scan ID…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9"
                />
              </div>
              <select
                data-testid="archive-risk-filter"
                value={risk}
                onChange={(e) => setRisk(e.target.value)}
                className="border border-slate-200 rounded-md px-3 text-sm bg-white"
              >
                <option value="">Any risk</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
                <option value="clear">Clear</option>
              </select>
              <Input
                data-testid="archive-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Input
                data-testid="archive-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="mt-3 text-xs text-slate-500">
              {loading ? 'Loading…' : <>Showing <strong>{rows.length}</strong> of <strong>{total}</strong> archived reports</>}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600">Generated</th>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600">Subject</th>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600">Scan ID</th>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600">Risk</th>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600">Matches</th>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600">Generator</th>
                  <th className="text-left px-4 py-2.5 font-medium text-slate-600">Signature</th>
                  <th className="text-right px-4 py-2.5 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-slate-500">
                      No archived reports yet. Reports appear here after you generate a PDF from a scan result.
                    </td>
                  </tr>
                ) : (
                  rows.map(r => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`archive-row-${r.id}`}>
                      <td className="px-4 py-2.5 text-slate-700 font-mono text-xs">
                        {r.generated_at ? new Date(r.generated_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-slate-900 font-medium">{r.subject || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{(r.scan_id || '').slice(0, 8)}…</td>
                      <td className="px-4 py-2.5">{riskBadge(r.risk_level)}</td>
                      <td className="px-4 py-2.5 text-slate-700">{r.match_count ?? 0}</td>
                      <td className="px-4 py-2.5 text-slate-600 text-xs">{r.generator_email || '—'}</td>
                      <td className="px-4 py-2.5 text-slate-500 font-mono text-xs" title={r.hmac}>
                        <ShieldCheck className="w-3.5 h-3.5 inline mr-1 text-emerald-600" />
                        {(r.hmac || '').slice(0, 10)}…
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`archive-download-${r.id}`}
                          onClick={() => downloadRow(r)}
                          className="gap-1"
                        >
                          <Download className="w-3.5 h-3.5" />
                          PDF
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
